// ------------------------------------------------------------------
// "What Trailer Do I Need?" — page content + SEO copy.
//
// The interactive AI quiz is the hook; THIS crawlable content is what
// actually ranks (the calculator-page formula). Keep it substantial and
// genuinely useful. No em-dashes in prose.
// ------------------------------------------------------------------

export const SITE = 'https://www.bigloads.com';
export const CANONICAL = 'https://www.bigloads.com/what-trailer-do-i-need';

export const TITLE = 'What Trailer Do I Need? Free Trailer Type Finder | BigLoads';
export const DESC =
  'Not sure what trailer to rent? Answer 4 quick questions and our AI trailer finder tells you the exact trailer type and size you need, whether your truck can tow it, and shows real trailers for rent near you.';

// The 4 quiz steps. `key` maps to the backend body fields.
export const STEPS = [
  {
    key: 'hauling',
    title: 'What are you hauling?',
    hint:
      'Be specific. e.g. "a riding mower and yard debris", "a 2005 Mustang", "3 tons of gravel", "a motorcycle".',
    placeholder: 'Tell us what you need to move...',
    long: true,
  },
  {
    key: 'towVehicle',
    title: 'What is your tow vehicle?',
    hint:
      'Year, make and model helps us check if it can safely tow. e.g. "2014 Ford F-150", "Jeep Grand Cherokee", "not sure yet".',
    placeholder: 'e.g. 2014 Ford F-150',
    long: false,
  },
  {
    key: 'distance',
    title: 'How far and how often?',
    hint: 'A rough idea is fine. e.g. "20 miles, one time", "cross-state move", "every weekend".',
    placeholder: 'e.g. about 30 miles, just once',
    long: false,
  },
  {
    key: 'zip',
    title: 'What is your ZIP code?',
    hint: 'So we can show you trailers available near you.',
    placeholder: 'e.g. 78006',
    long: false,
    zip: true,
  },
];

// Plain-English verdict labels for the tow-fit result.
export const TOW_FIT_LABEL = {
  good_fit: { label: 'Good fit', tone: 'good' },
  borderline: { label: 'Close to the limit', tone: 'warn' },
  over_limit: { label: 'Likely too heavy', tone: 'bad' },
  need_more_info: { label: 'Tell us more', tone: 'info' },
};

// ---- SEO content wrapper (below the quiz) --------------------------

export const INTRO_PARAS = [
  'Renting a trailer is easy once you know which type you need. Picking the wrong one is where most people get stuck. Too small and your load will not fit or ride safely. Too big and you are paying for capacity you do not need, or worse, hauling something your vehicle cannot handle.',
  'This finder does the thinking for you. Tell us what you are hauling, what you are towing it with, and where you are, and our AI matches you to the right trailer type and size, gives you an honest read on whether your vehicle can tow it, and shows you real trailers available for rent from local owners near you.',
];

// Trailer type breakdown cards. Each is genuinely informative + internally
// linked so the page ranks for "what is a X trailer" and feeds the type hubs.
export const TYPE_GUIDE = [
  {
    key: 'utility-trailer',
    name: 'Utility trailer',
    best: 'Moving, landscaping, mowers, furniture, general hauling',
    detail:
      'The all-rounder. An open deck with low sides, usually 4x6 up to 6x12 feet. Great for a couple of appliances, a riding mower, mulch bags, or a small apartment move. Light enough that most SUVs and half-ton trucks tow it comfortably.',
    hub: '/s?pub_trailerType=has_any:utility-trailer',
  },
  {
    key: 'dump-trailer',
    name: 'Dump trailer',
    best: 'Dirt, gravel, mulch, demolition debris, junk removal',
    detail:
      'A heavy-duty box that hydraulically tilts to dump its load. If you are hauling loose material by the ton, this saves your back and your day. Look at the cubic-yard rating and remember that gravel and wet soil get heavy fast, so tow capacity matters here.',
    hub: '/dump-trailer-rental',
  },
  {
    key: 'car-hauler',
    name: 'Car hauler',
    best: 'Cars, trucks, and other vehicles',
    detail:
      'An open flatbed with ramps built to load a vehicle. Usually 18 to 20 feet with a 7,000 lb or higher capacity. Make sure the deck length and weight rating clear your specific vehicle, and that your truck is rated to tow the loaded weight.',
    hub: '/car-hauler-rental',
  },
  {
    key: 'enclosed-trailer',
    name: 'Enclosed / cargo trailer',
    best: 'Moving, valuables, dry cargo, weather-sensitive loads',
    detail:
      'A box on wheels with a door, locking and weatherproof. The right call for a real move, tools, band gear, or anything you do not want exposed to rain or eyes. Heavier empty than an open trailer, so factor that into your tow math.',
    hub: '/s?pub_trailerType=has_any:enclosed-trailer',
  },
  {
    key: 'flatbed-trailer',
    name: 'Flatbed trailer',
    best: 'Equipment, building materials, lumber, oversized loads',
    detail:
      'An open deck with no sides, so you can load from any angle and carry things wider than the trailer. Ideal for lumber, steel, pallets, or an odd-shaped load. Strap-down points and deck length are what you care about.',
    hub: '/s?pub_trailerType=has_any:flatbed-trailer',
  },
  {
    key: 'equipment-trailer',
    name: 'Equipment trailer',
    best: 'Tractors, skid steers, mini excavators, heavy machines',
    detail:
      'Built like a flatbed but heavier duty, with fold-down ramps rated for rolling on machinery. If you are moving a skid steer or a compact tractor, this is the one. Check the ramp and axle ratings against your machine weight.',
    hub: '/s?pub_trailerType=has_any:equipment-trailer',
  },
  {
    key: 'motorcycle-trailer',
    name: 'Motorcycle trailer',
    best: 'Motorcycles and small powersports',
    detail:
      'Small, light, and set up with a wheel chock and tie-down points to carry a bike safely. Many crossovers and cars can tow one. Great for a track day, a bike you just bought, or a breakdown recovery.',
    hub: '/motorcycle-trailer-rental',
  },
  {
    key: 'tow-dolly',
    name: 'Tow dolly',
    best: 'Flat-towing a front-wheel-drive vehicle',
    detail:
      'Lifts the front wheels of a car so you can tow it behind an RV or truck without a full car hauler. Lighter and cheaper than a trailer, but only works for front-wheel-drive vehicles. Confirm your car qualifies before you book.',
    hub: '/s?pub_trailerType=has_any:tow-dolly',
  },
];

// "Can my truck tow it?" quick guidance block (rich, crawlable, HowTo schema).
export const TOW_STEPS = [
  {
    name: 'Find your vehicle tow rating',
    text:
      'Check your owner manual or the door-jamb sticker for the maximum tow rating. Do not trust a generic number online, since trim, engine, and tow package all change it.',
  },
  {
    name: 'Add up the loaded weight',
    text:
      'Add the empty trailer weight to the weight of what you are hauling. An empty utility trailer might be 800 lb, a dump trailer 2,500 lb or more, before you add a single pound of cargo.',
  },
  {
    name: 'Stay under 80 percent',
    text:
      'For a comfortable, safe tow, keep the loaded weight at or below about 80 percent of your rating. Running right at the max makes braking and handling worse, especially on hills.',
  },
  {
    name: 'Check tongue weight and brakes',
    text:
      'Tongue weight should be roughly 10 to 15 percent of the trailer weight. Heavier trailers need working trailer brakes and a properly rated hitch and ball.',
  },
];

export const FAQ = [
  {
    q: 'How do I know what size trailer I need?',
    a:
      'Match the trailer to your heaviest and largest single item, then add a little margin. Our free trailer finder above does this for you, factoring in what you are hauling and how far you are taking it.',
  },
  {
    q: 'Can my truck or SUV tow the trailer?',
    a:
      'It depends on your vehicle tow rating and the loaded weight of the trailer. Enter your tow vehicle in the finder and we give you an honest read on whether it can handle the recommended trailer safely.',
  },
  {
    q: 'Is the trailer finder free?',
    a:
      'Yes. The finder is completely free, and renting a trailer on BigLoads never costs a membership fee for renters. You just pay the owner for the rental.',
  },
  {
    q: 'What if there are no trailers near me yet?',
    a:
      'Leave your email and we will notify you the moment a matching trailer is listed in your area. BigLoads is a local marketplace that grows as owners in each area list their trailers.',
  },
  {
    q: 'Do I need a special license to rent a trailer?',
    a:
      'For most standard trailers behind a regular vehicle, no special license is required, but towing laws vary by state. Check your state towing rules for weight, braking, and speed limits before you haul.',
  },
];
